import { supabase } from '../supabase'
import type { AiPlayerRow, RoomPlayerRow } from '../database.types'

/** Catalog of personalities the host can drop into a room. */
export async function fetchAiPlayers(): Promise<AiPlayerRow[]> {
  const { data, error } = await supabase
    .from('ai_players')
    .select('*')
    .eq('is_active', true)
    .order('name')
  if (error) throw error
  return (data ?? []) as AiPlayerRow[]
}

/** Host-only. Drops an AI row into the lobby using the chosen persona. */
export async function addAiPlayer(
  roomId: string,
  aiPlayerId: string,
): Promise<RoomPlayerRow> {
  const { data, error } = await supabase.rpc('add_ai_player', {
    p_room_id: roomId,
    p_ai_player_id: aiPlayerId,
  })
  if (error) throw error
  return data as RoomPlayerRow
}

/** Host-only. Removes an AI row from the lobby. */
export async function removeAiPlayer(playerId: string): Promise<void> {
  const { error } = await supabase.rpc('remove_ai_player', {
    p_player_id: playerId,
  })
  if (error) throw error
}

/**
 * Host-only. Submits a 3-course meal on behalf of an AI player.
 * Used by the legacy all-courses-at-once flow; the current per-course
 * flow uses `submitCourseAsAi` below.
 */
export async function submitCoursesAsAi(
  playerId: string,
  c1: { text: string; ids: string[] },
  c2: { text: string; ids: string[] },
  c3: { text: string; ids: string[] },
): Promise<void> {
  const { error } = await supabase.rpc('submit_courses_as_ai', {
    p_player_id: playerId,
    p_c1_text: c1.text, p_c1_ids: c1.ids,
    p_c2_text: c2.text, p_c2_ids: c2.ids,
    p_c3_text: c3.text, p_c3_ids: c3.ids,
  })
  if (error) throw error
}

/**
 * Host-only per-course submission on behalf of an AI. Called once per
 * (player, course) by the autoplay effect during the `building` phase.
 */
export async function submitCourseAsAi(
  playerId: string,
  course: 1 | 2 | 3,
  text: string,
  ids: string[],
): Promise<void> {
  const { error } = await supabase.rpc('submit_course_as_ai', {
    p_player_id: playerId,
    p_course:    course,
    p_text:      text,
    p_ids:       ids,
  })
  if (error) throw error
}

/**
 * Host-only. Casts a course vote on behalf of an AI player.
 * Used by the autoplay loop during the `presenting` phase.
 */
export async function castCourseVoteAsAi(
  playerId: string,
  submissionId: string,
  course: 1 | 2 | 3,
  wouldEat: boolean,
): Promise<void> {
  const { error } = await supabase.rpc('cast_course_vote_as_ai', {
    p_player_id: playerId,
    p_submission_id: submissionId,
    p_course: course,
    p_would_eat: wouldEat,
  })
  if (error) throw error
}

/**
 * Asks the `ai-player-turn` edge function to generate ONE course for
 * an AI player via a real LLM (Haiku by default). The edge function
 * partitions the hand server-side using a seed derived from
 * (room, player), so we always get the exact bucket the physical
 * game would use.
 *
 * Returns `{ text, ids }`. Client writes with `submitCourseAsAi`.
 * If the edge function fails, the caller should fall back to the
 * local template path so the game doesn't stall.
 */
export async function generateCourseFromAi(
  roomId: string,
  playerId: string,
  course: 1 | 2 | 3,
): Promise<{ text: string; ids: string[] }> {
  const { data, error } = await supabase.functions.invoke('ai-player-turn', {
    body: { room_id: roomId, player_id: playerId, course },
  })
  if (error) throw error
  const text = typeof data?.text === 'string' ? data.text.trim() : ''
  const ids = Array.isArray(data?.ids) ? (data.ids as string[]) : []
  return { text, ids }
}
